import type { Atlas, BuntuOptions, BuntuPrefs, Corner, Mode, Spring } from './types';
import { loadAtlas, preload } from './atlas';
import { Renderer, type FrameState } from './renderer';
import { inTimeWindow, type AdminMessage, type BuntuTenantPolicy } from './policy';

/**
 * Buntu's brain: timers, state machine, and the single animation loop.
 *
 * Holds no React and touches no DOM directly -- it drives a Renderer. That
 * separation is what lets the same engine run inside a desktop overlay window
 * later without changes.
 */

/** Poses the engine can display, preloaded so swaps never flash. */
const USED_POSES = [
  'idle', 'move-left', 'move-right', 'cheer', 'waiting',
  'success', 'puzzled', 'alert', 'thinking',
];

/** Click reactions — one is picked at random when the user taps Buntu. */
const EXPRESS_POSES = ['cheer', 'success', 'puzzled', 'alert', 'thinking'] as const;

/** Distance, as a fraction of viewport width, at which gaze hits full deflection. */
const GAZE_REACH = 0.42;
/** No pointer movement for this long and Buntu looks around on its own. */
const IDLE_GLANCE_MS = 3200;
/** No input at all for this long and the break timer stops accruing. */
const AWAY_MS = 3 * 60 * 1000;
/** Bounds of the randomised blink interval. */
const BLINK_MIN_MS = 2400;
const BLINK_MAX_MS = 6200;
const BLINK_DUR_MS = 150;
/** Time between footfalls in the walk cycle. */
const STEP_MS = 250;
const WALK_SPEED = 145; // px per second
/** After a drop: how fast he climbs down to the floor. */
const CLIMB_SPEED = 260; // px per second
/** After a drop: how fast he runs along the floor to his corner. */
const RUN_HOME_SPEED = 210; // px per second
const MARGIN = 22;
/**
 * Desktop overlay covers the whole screen including the Windows taskbar.
 * Lift parked Buntu above the bar so the clock/date stay readable; keep him
 * on the right edge (no extra left shift — that looked stranded mid-tray).
 */
const TASKBAR_CLEAR = 56;
/** Pointer travel before a press becomes a drag instead of a click. */
const DRAG_THRESHOLD_PX = 7;
/** How long a click expression stays as a full avatar. */
const EXPRESS_MS = 2200;
/** Grace period for a second click before the first opens the options. */
const DOUBLE_CLICK_MS = 260;
/** Close enough to the floor / corner to finish that return leg. */
const ARRIVE_PX = 4;

const BREAK_LINES = [
  "That's an hour at the screen. Stand up and stretch for a minute.",
  "An hour already. Roll your shoulders, look out a window.",
  "You've been at it an hour. Give your eyes a short break.",
];

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function spring(value: number, stiffness: number): Spring {
  return { value, target: value, stiffness };
}

/** Frame-rate independent easing toward the target. */
function advance(s: Spring, dt: number): void {
  s.value += (s.target - s.value) * (1 - Math.pow(1 - s.stiffness, dt * 60));
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Midnight tonight, used to scope a "hide for today" preference. */
export function endOfToday(now = Date.now()): number {
  const d = new Date(now);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

type Phase = 'idle' | 'enter' | 'talk' | 'exit';
/**
 * User-driven interaction layered over the hourly break phases.
 * After a drop: climb down to the floor, then run to the bottom-right.
 */
type Interact =
  | 'none'
  | 'press'
  | 'drag'
  | 'express'
  | 'return-climb'
  | 'return-run';

export class BuntuEngine {
  private readonly opts: BuntuOptions;
  private prefs: BuntuPrefs;
  private renderer: Renderer | null = null;
  private atlas: Atlas | null = null;

  private raf = 0;
  private last = 0;
  private lastRaf = 0;
  private watchdogTimer = 0;
  private destroyed = false;

  // Motion state.
  private readonly px: Spring = spring(0, 0.14);
  private readonly py: Spring = spring(0, 0.14);
  private readonly gx: Spring = spring(0, 0.11);
  private readonly gy: Spring = spring(0, 0.11);
  private clock = 0;

  private mode: Mode = 'head';
  private phase: Phase = 'idle';
  private phaseT = 0;
  /** Viewport x the current walk phase is heading for. */
  private stopX = 0;
  private facing: 1 | -1 = 1;
  private poseName = 'idle';
  private bubble: string | null = null;
  /** The reminder chosen for this break, shown once he stops to speak. */
  private line = '';
  private opacity = 0;

  // Gaze and blink.
  private pointer: { x: number; y: number } | null = null;
  private lastPointerMove = 0;
  private nextGlance = 0;
  private blinkUntil = 0;
  private nextBlink = 0;
  private blink = 0;

  // Drag / click.
  private interact: Interact = 'none';
  private pressPtrId: number | null = null;
  private clickTimer = 0;
  private pressAt = { x: 0, y: 0 };
  private grabOffset = { x: 0, y: 0 };
  private expressUntil = 0;
  /** X held while climbing down after a drop (avoids sideways drift). */
  private climbX = 0;

  // Break accounting.
  private activeMs = 0;
  private lastInput = Date.now();
  private reduced = false;

  /** Tenant policy from the Hub; null until the companion loads runtime. */
  private policy: BuntuTenantPolicy | null = null;
  /** True when this Hub user is a CLIENT_ADMIN and policy requires Buntu. */
  private buntuRequired = false;
  private adminMsg: AdminMessage | null = null;
  private onAdminAck: ((id: string) => void) | null = null;
  private messageHistory: AdminMessage[] = [];
  private messageQueue: Array<{ msg: AdminMessage; onAck: ((id: string) => void) | null }> = [];
  private inboxExtra = 0;

  constructor(opts: BuntuOptions) {
    this.opts = opts;
    this.prefs = opts.prefs;
    this.messageHistory = loadMessageHistory();
  }

  async start(): Promise<void> {
    this.atlas = await loadAtlas(this.opts.atlasUrl);
    if (this.destroyed) return;
    await preload(this.atlas, this.opts.assetUrl, USED_POSES);
    if (this.destroyed) return;

    this.renderer = new Renderer(this.opts.root, this.atlas, this.opts.assetUrl);
    this.renderer.onDismiss = () => this.dismissCurrentMessage();
    this.renderer.onAction = (action) => {
      if (action === 'messages') this.openMessages();
      else if (action === 'snooze') {
        // Wellness snooze is fine even when presence is required.
        if (this.policyAllowsOptOut() || this.buntuRequired) {
          this.snoozeRemindersOnly(60 * 60 * 1000);
        }
      } else if (!this.buntuRequired && this.policyAllowsOptOut() && this.policyAllowsMinimize()) {
        this.hideForToday();
      }
    };
    this.renderer.onRestore = () => this.show();
    this.reduced = prefersReducedMotion();

    // Prefer the last policy-driven corner from prefs (Hub admin default_corner).
    // Do not force bottom-right — that overwrote dashboard changes on every start.

    const home = this.homePosition();
    this.px.value = this.px.target = home.x;
    this.py.value = this.py.target = home.y;

    this.bind();
    this.bindStage();
    this.last = performance.now();
    this.lastRaf = this.last;
    this.nextBlink = this.last + 900;
    this.raf = requestAnimationFrame(this.tick);
    this.watchdogTimer = window.setInterval(this.watchdog, 200);
  }

  // --- public controls ------------------------------------------------------

  /**
   * Applies tenant policy from GET /buntu/runtime.
   * @param required when true (tenant admin + required_for_admins), opt-out is blocked.
   */
  applyPolicy(policy: BuntuTenantPolicy | null, required = false): void {
    this.policy = policy;
    this.buntuRequired = required;
    if (!policy) return;

    const corner = policy.presence?.default_corner;
    if (corner && corner !== this.prefs.corner) {
      this.update({ corner });
      // Move to the new admin corner when idle — prefs alone left him parked.
      this.moveToPolicyHome();
    }
    const mins = policy.wellness?.interval_minutes;
    if (typeof mins === 'number' && mins > 0 && mins !== this.prefs.intervalMinutes) {
      this.update({ intervalMinutes: mins });
    }
    if (policy.presence?.respect_reduced_motion !== false) {
      this.reduced = prefersReducedMotion();
    } else {
      this.reduced = false;
    }
    // Required admins cannot stay tucked away behind the restore chip.
    if (this.buntuRequired && this.prefs.minimized) {
      this.update({ minimized: false, hiddenUntil: null });
    }
  }

  /** Glide to the configured corner when not mid-message / drag / return. */
  private moveToPolicyHome(): void {
    if (
      this.interact === 'drag' ||
      this.interact === 'press' ||
      this.interact === 'return-climb' ||
      this.interact === 'return-run'
    ) {
      return;
    }
    if (this.phase === 'talk' && this.adminMsg) return;
    const home = this.homePosition();
    this.px.stiffness = 0.16;
    this.py.stiffness = 0.16;
    this.px.target = home.x;
    this.py.target = home.y;
  }

  /**
   * Show an admin broadcast at bottom-center. A message arriving while one is
   * already being spoken queues behind it, so a backlog of missed messages is
   * announced one after another instead of overwriting itself.
   */
  showAdminMessage(msg: AdminMessage, onAck?: (id: string) => void): void {
    if (!msg?.body) return;
    this.rememberMessage(msg);
    if (!this.policyMessagingEnabled()) return;
    if (this.prefs.minimized || this.prefs.hiddenUntil || this.prefs.snoozedUntil) {
      this.show();
    }
    if (this.adminMsg?.id === msg.id && this.bubble) return;
    if (this.messageQueue.some((q) => q.msg.id === msg.id)) return;

    if (this.phase === 'talk' && this.adminMsg) {
      this.messageQueue.push({ msg, onAck: onAck || null });
      return;
    }
    this.speakAdminMessage(msg, onAck || null);
  }

  private speakAdminMessage(msg: AdminMessage, onAck: ((id: string) => void) | null): void {
    this.interact = 'none';
    this.adminMsg = msg;
    this.onAdminAck = onAck;
    this.activeMs = 0;
    this.line = msg.body;

    const poseByPriority: Record<string, string> = {
      urgent: 'alert',
      important: 'puzzled',
      info: 'cheer',
    };

    // Skip enter/exit walk — previous path often never reached "talk", so the
    // bubble never appeared even though the host logged "delivering to UI".
    this.phase = 'talk';
    this.phaseT = 0;
    this.mode = 'walk';
    this.poseName = poseByPriority[msg.priority] || 'cheer';
    this.bubble = this.line;

    // Messages are read at bottom-center rather than from the parked corner.
    const spot = this.messagePosition();
    this.px.stiffness = 0.16;
    this.py.stiffness = 0.16;
    this.px.target = spot.x;
    this.py.target = spot.y;
    this.facing = spot.x >= this.px.value ? 1 : -1;
    // The exit walk reads stopX; without the enter phase it would be stale.
    this.stopX = this.homePosition().x;
    // Paint now instead of waiting on the next frame, which the overlay window
    // may not get if the compositor has parked it.
    this.renderer?.apply(this.frame());
  }

  /**
   * Reader closed the bubble: acknowledge it now so the Hub marks it read and
   * stops re-sending, then move straight on to anything still queued.
   */
  dismissCurrentMessage(): void {
    if (!this.adminMsg) return;
    this.ackCurrentMessage();
    this.bubble = null;
    this.phaseT = 0;
    if (this.nextQueuedMessage()) return;
    this.endBreak();
    this.renderer?.apply(this.frame());
  }

  /** True when another queued message was started. */
  private nextQueuedMessage(): boolean {
    const next = this.messageQueue.shift();
    if (!next) return false;
    this.speakAdminMessage(next.msg, next.onAck);
    return true;
  }

  /** Record a message for the Messages panel without necessarily speaking it. */
  rememberMessage(msg: AdminMessage): void {
    if (!msg?.id || !msg?.body) return;
    if (this.messageHistory.some((m) => m.id === msg.id)) return;
    this.messageHistory = [
      {
        id: msg.id,
        body: msg.body,
        priority: msg.priority || 'info',
        delivery: msg.delivery || 'once',
        created_by: msg.created_by || null,
        sent_at: msg.sent_at || new Date().toISOString(),
      },
      ...this.messageHistory,
    ].slice(0, HISTORY_MAX);
    saveMessageHistory(this.messageHistory);
  }

  /** Replace/merge the Messages panel contents with server history. */
  mergeHistory(items: AdminMessage[]): void {
    if (!Array.isArray(items)) return;
    const byId = new Map<string, AdminMessage>();
    for (const m of [...items, ...this.messageHistory]) {
      if (!m?.id || !m?.body) continue;
      if (byId.has(m.id)) continue;
      byId.set(m.id, {
        id: m.id,
        body: m.body,
        priority: m.priority || 'info',
        delivery: m.delivery || 'once',
        created_by: m.created_by || null,
        sent_at: m.sent_at || null,
      });
    }
    const merged: AdminMessage[] = [];
    byId.forEach((m) => merged.push(m));
    this.messageHistory = merged
      .sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''))
      .slice(0, HISTORY_MAX);
    saveMessageHistory(this.messageHistory);
    // Keep the reader where they were if they had paged into older messages.
    if (this.renderer?.isInboxOpen()) this.openMessages(this.inboxExtra);
  }

  /**
   * Opens the Messages panel (who / what / when). Today's messages show by
   * default; `extra` older ones are revealed 20 at a time on request.
   */
  openMessages(extra = 0): void {
    const today: AdminMessage[] = [];
    const older: AdminMessage[] = [];
    for (const m of this.messageHistory) {
      (isToday(m.sent_at) ? today : older).push(m);
    }
    this.inboxExtra = Math.max(0, Math.min(extra, older.length));
    const shown = today.concat(older.slice(0, this.inboxExtra));
    const rows = shown.map((m) => ({
      from: (m.created_by || 'Admin').toString(),
      body: m.body,
      when: isToday(m.sent_at)
        ? formatMessageTime(m.sent_at)
        : formatMessageWhen(m.sent_at),
    }));
    const remaining = older.length - this.inboxExtra;
    this.renderer?.showInbox(rows, {
      emptyText: older.length
        ? 'No messages today.'
        : 'No messages yet.',
      moreLabel:
        remaining > 0
          ? `Older messages (${Math.min(INBOX_PAGE, remaining)} more)`
          : null,
      onMore: () => this.openMessages(this.inboxExtra + INBOX_PAGE),
    });
  }

  /** Runs the stretch reminder immediately. */
  takeBreakNow(): void {
    if (this.phase === 'idle' && this.policyWellnessEnabled()) this.beginBreak();
  }

  private policyEnabled(): boolean {
    if (!this.policy) return true;
    return !!this.policy.enablement?.enabled;
  }

  private policyWellnessEnabled(): boolean {
    if (!this.policy) return true;
    if (!this.policyEnabled()) return false;
    if (this.policy.branding?.messaging_only) return false;
    return !!this.policy.wellness?.enabled;
  }

  private policyMessagingEnabled(): boolean {
    if (!this.policy) return true;
    if (!this.policyEnabled()) return false;
    return this.policy.messaging?.enabled !== false;
  }

  private policyAllowsOptOut(): boolean {
    return this.policy?.enablement?.allow_user_opt_out !== false;
  }

  private policyAllowsMinimize(): boolean {
    return this.policy?.privacy?.allow_minimize !== false;
  }

  private inQuietHours(): boolean {
    const w = this.policy?.wellness;
    if (!w?.quiet_hours_enabled) return false;
    return inTimeWindow(w.quiet_hours_start, w.quiet_hours_end);
  }

  private inDnd(): boolean {
    const p = this.policy?.privacy;
    if (!p?.dnd_enabled) return false;
    return inTimeWindow(p.dnd_start, p.dnd_end);
  }

  private breakLines(): string[] {
    const pack = this.policy?.wellness?.message_pack;
    if (Array.isArray(pack) && pack.length) return pack;
    return BREAK_LINES;
  }

  /** Restores Buntu from the minimize chip (undo snooze/hide by mistake). */
  show(): void {
    this.activeMs = 0;
    this.interact = 'none';
    this.mode = 'head';
    this.poseName = 'idle';
    this.bubble = null;
    this.phase = 'idle';
    this.update({
      minimized: false,
      hiddenUntil: null,
      snoozedUntil: null,
    });
    const home = this.homePosition();
    this.px.stiffness = 0.18;
    this.py.stiffness = 0.18;
    this.px.target = home.x;
    this.py.target = home.y;
    this.renderer?.setMenuLocked(false);
  }

  /**
   * Pauses break reminders for a while and tucks Buntu into the restore chip
   * so a mistaken snooze is one click away from undo.
   */
  snooze(ms: number): void {
    if (this.buntuRequired) {
      this.snoozeRemindersOnly(ms);
      return;
    }
    this.endBreak();
    this.activeMs = 0;
    this.interact = 'none';
    this.update({
      minimized: true,
      snoozedUntil: Date.now() + ms,
      hiddenUntil: null,
    });
  }

  /** Snooze wellness only — keep the floating companion visible. */
  snoozeRemindersOnly(ms: number): void {
    this.endBreak();
    this.activeMs = 0;
    this.interact = 'none';
    this.update({ snoozedUntil: Date.now() + ms });
  }

  /**
   * Tucks Buntu away for the rest of the day as a restore chip (never a
   * hard disappear — click "Show Buntu" to bring him back).
   */
  hideForToday(): void {
    if (this.buntuRequired) return;
    this.endBreak();
    this.interact = 'none';
    this.update({
      minimized: true,
      hiddenUntil: endOfToday(),
      snoozedUntil: null,
    });
  }

  setCorner(corner: Corner): void {
    this.update({ corner });
  }

  /** Full pet is on screen (not minimized to the restore chip). */
  get visible(): boolean {
    return !this.prefs.minimized;
  }

  private update(patch: Partial<BuntuPrefs>): void {
    this.prefs = { ...this.prefs, ...patch };
    this.opts.onPrefsChange(this.prefs);
  }

  // --- event wiring ---------------------------------------------------------

  private onPointerMove = (e: PointerEvent | MouseEvent): void => {
    this.pointer = { x: e.clientX, y: e.clientY };
    this.lastPointerMove = performance.now();
    this.lastInput = Date.now();

    if (!(e instanceof PointerEvent)) return;
    if (this.pressPtrId !== null && e.pointerId === this.pressPtrId) {
      this.stepPressMove(e.clientX, e.clientY);
    }
  };

  private onInput = (): void => {
    this.lastInput = Date.now();
  };

  private onVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      // Skip the wall-clock gap so a backgrounded tab cannot jump the loop.
      this.last = performance.now();
      this.lastInput = Date.now();
    }
  };

  private onResize = (): void => {
    if (this.interact === 'drag' || this.phase !== 'idle') return;
    const home = this.homePosition();
    this.px.target = home.x;
    this.py.target = home.y;
  };

  private onStageDown = (e: PointerEvent): void => {
    if (e.button !== 0) return;
    if (!this.visible) return;
    // Don't interrupt an in-progress stretch walk mid-stride.
    if (this.phase !== 'idle' && this.interact === 'none') return;
    // Don't interrupt a drop return, or start a second grab while held.
    if (
      this.interact === 'drag' ||
      this.interact === 'press' ||
      this.interact === 'return-climb' ||
      this.interact === 'return-run'
    ) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    // The menu is no longer closed here: a press may turn out to be the single
    // click that toggles it. Dragging closes it instead (see beginDrag).

    this.pressPtrId = e.pointerId;
    this.pressAt = { x: e.clientX, y: e.clientY };
    this.grabOffset = {
      x: e.clientX - this.px.value,
      y: e.clientY - this.py.value,
    };
    this.interact = 'press';
    this.lastInput = Date.now();

    try {
      this.renderer?.stage.setPointerCapture(e.pointerId);
    } catch {
      /* capture is best-effort on older browsers */
    }
  };

  private onStageUp = (e: PointerEvent): void => {
    if (this.pressPtrId === null || e.pointerId !== this.pressPtrId) return;
    this.finishPress(e.clientX, e.clientY);
  };

  private onStageCancel = (e: PointerEvent): void => {
    if (this.pressPtrId === null || e.pointerId !== this.pressPtrId) return;
    this.finishPress(e.clientX, e.clientY);
  };

  private bind(): void {
    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    window.addEventListener('keydown', this.onInput, { passive: true });
    window.addEventListener('wheel', this.onInput, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  private bindStage(): void {
    const stage = this.renderer?.stage;
    if (!stage) return;
    stage.addEventListener('pointerdown', this.onStageDown);
    stage.addEventListener('pointerup', this.onStageUp);
    stage.addEventListener('pointercancel', this.onStageCancel);
  }

  private unbind(): void {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('keydown', this.onInput);
    window.removeEventListener('wheel', this.onInput);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    const stage = this.renderer?.stage;
    if (stage) {
      stage.removeEventListener('pointerdown', this.onStageDown);
      stage.removeEventListener('pointerup', this.onStageUp);
      stage.removeEventListener('pointercancel', this.onStageCancel);
    }
  }

  /**
   * Once the pointer travels past the click threshold, Buntu "lifts" into
   * full-avatar form and follows the cursor, clamped inside the window.
   */
  private stepPressMove(x: number, y: number): void {
    if (this.interact === 'press') {
      const dx = x - this.pressAt.x;
      const dy = y - this.pressAt.y;
      if (dx * dx + dy * dy < DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) return;
      this.beginDrag();
    }
    if (this.interact !== 'drag') return;

    const size = this.renderer?.walkSize ?? { w: 144, h: 176 };
    const halfW = size.w / 2;
    const nx = clamp(x - this.grabOffset.x, MARGIN + halfW, window.innerWidth - MARGIN - halfW);
    const ny = clamp(y - this.grabOffset.y, MARGIN + size.h, window.innerHeight - TASKBAR_CLEAR);
    // Follow the pointer tightly while held — springing here would lag behind.
    this.px.stiffness = 1;
    this.py.stiffness = 1;
    this.px.value = this.px.target = nx;
    this.py.value = this.py.target = ny;
  }

  private beginDrag(): void {
    if (this.policy?.presence?.drag_and_drop_return === false) {
      // Policy allows grab but not the climb/run — still lift as full avatar.
    }
    // Cancel a stretch walk if the user decides to pick him up mid-break.
    if (this.phase !== 'idle') this.endBreak(false);
    window.clearTimeout(this.clickTimer);
    this.clickTimer = 0;
    this.renderer?.closeMenu();
    this.renderer?.setMenuLocked(true);
    this.interact = 'drag';
    this.mode = 'walk';
    this.poseName = 'waiting';
    this.bubble = null;
    this.gx.target = 0;
    this.gy.target = 0;
  }

  private finishPress(_x: number, _y: number): void {
    const was = this.interact;
    this.pressPtrId = null;

    if (was === 'drag') {
      this.beginReturnHome();
      return;
    }

    if (was === 'press') {
      // Single click opens the options; a second click inside the double-click
      // window cancels that and plays an expression instead.
      if (this.clickTimer) {
        window.clearTimeout(this.clickTimer);
        this.clickTimer = 0;
        this.beginExpress();
        return;
      }
      this.interact = 'none';
      this.renderer?.setMenuLocked(false);
      this.clickTimer = window.setTimeout(() => {
        this.clickTimer = 0;
        this.renderer?.toggleMenu();
      }, DOUBLE_CLICK_MS);
      return;
    }
  }

  /**
   * Drop: climb down to the floor first, then run along it to the
   * bottom-right corner — not a spring teleport.
   */
  private beginReturnHome(): void {
    this.bubble = null;
    this.phase = 'idle';
    this.gx.target = 0;
    this.gy.target = 0;

    if (this.reduced || this.policy?.presence?.drag_and_drop_return === false) {
      this.settleHome();
      return;
    }

    const floorY = this.floorY();
    // Already near the floor → skip the climb and just run home.
    if (this.py.value >= floorY - ARRIVE_PX) {
      this.py.value = this.py.target = floorY;
      this.beginRunHome();
      return;
    }

    this.interact = 'return-climb';
    this.mode = 'walk';
    this.poseName = 'waiting';
    this.climbX = this.px.value;
    this.px.stiffness = 1;
    this.py.stiffness = 1;
  }

  private beginRunHome(): void {
    const home = this.homePosition();
    this.interact = 'return-run';
    this.mode = 'walk';
    this.stopX = home.x;
    this.facing = home.x >= this.px.value ? 1 : -1;
    this.py.value = this.py.target = this.floorY();
    // Already in the corner → settle without a pointless shuffle.
    if (Math.abs(this.px.value - home.x) <= ARRIVE_PX) {
      this.settleHome();
    }
  }

  /** Floor line the pet runs on (feet on the bottom margin). */
  private floorY(): number {
    return window.innerHeight - MARGIN;
  }

  /** Arrival: floating head in the corner again. */
  private settleHome(): void {
    this.interact = 'none';
    this.mode = 'head';
    this.poseName = 'idle';
    this.bubble = null;
    this.phase = 'idle';
    this.facing = 1;
    this.px.stiffness = 0.18;
    this.py.stiffness = 0.18;
    const home = this.homePosition();
    this.px.value = this.px.target = home.x;
    this.py.value = this.py.target = home.y;
    this.renderer?.setMenuLocked(false);
  }

  /** Climb straight down, then hand off to the floor run. */
  private stepReturnClimb(dt: number): void {
    this.mode = 'walk';
    this.poseName = 'waiting';
    const floorY = this.floorY();
    this.py.value = Math.min(floorY, this.py.value + CLIMB_SPEED * dt);
    this.py.target = this.py.value;
    // Wiggle around the drop X — never accumulate, or he drifts sideways.
    this.px.value = this.climbX + Math.sin(this.clock * 14) * 3;
    this.px.target = this.px.value;

    if (this.py.value >= floorY - ARRIVE_PX) {
      this.px.value = this.px.target = this.climbX;
      this.py.value = this.py.target = floorY;
      this.beginRunHome();
    }
  }

  /** Run along the floor to the bottom-right corner. */
  private stepReturnRun(dt: number): void {
    this.mode = 'walk';
    const dir = this.facing;
    this.px.value += dir * RUN_HOME_SPEED * dt;
    this.px.target = this.px.value;
    this.py.value = this.py.target = this.floorY();
    this.poseName =
      Math.floor((this.clock * 1000) / STEP_MS) % 2 === 0 ? 'move-left' : 'move-right';

    const reached =
      dir > 0
        ? this.px.value >= this.stopX - ARRIVE_PX
        : this.px.value <= this.stopX + ARRIVE_PX;
    if (reached) this.settleHome();
  }

  /** Click reaction: pop into a full-body expression, then settle home. */
  private beginExpress(): void {
    if (this.policy?.presence?.click_expressions === false) {
      this.interact = 'none';
      this.renderer?.setMenuLocked(false);
      return;
    }
    if (this.phase !== 'idle') this.endBreak(false);
    const poseName =
      EXPRESS_POSES[Math.floor(Math.random() * EXPRESS_POSES.length)];
    this.interact = 'express';
    this.mode = this.reduced ? 'head' : 'walk';
    this.poseName = this.reduced ? 'idle' : poseName;
    this.bubble = null;
    this.expressUntil = performance.now() + EXPRESS_MS;
    this.gx.target = 0;
    this.gy.target = 0;
    // Bounce him slightly toward the centre so the pose is readable.
    const home = this.homePosition();
    this.px.stiffness = 0.16;
    this.py.stiffness = 0.16;
    this.px.target = home.x - Math.min(90, window.innerWidth * 0.08);
    this.py.target = home.y;
  }

  private endExpress(): void {
    this.interact = 'none';
    this.mode = 'head';
    this.poseName = 'idle';
    this.px.stiffness = 0.18;
    this.py.stiffness = 0.18;
    const home = this.homePosition();
    this.px.target = home.x;
    this.py.target = home.y;
    this.renderer?.setMenuLocked(false);
  }

  // --- layout ---------------------------------------------------------------

  /** Where admin messages are spoken — bottom-center, above the taskbar. */
  private messagePosition(): { x: number; y: number } {
    return { x: window.innerWidth / 2, y: window.innerHeight - TASKBAR_CLEAR };
  }

  private homePosition(): { x: number; y: number } {
    const size = this.renderer?.headSize ?? { w: 100, h: 132 };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = MARGIN + size.w / 2;
    const right = vw - MARGIN - size.w / 2;
    const bottom = vh - TASKBAR_CLEAR;
    const top = MARGIN + size.h;
    switch (this.prefs.corner) {
      case 'bottom-left':
        return { x: left, y: bottom };
      case 'top-right':
        return { x: right, y: top };
      case 'top-left':
        return { x: left, y: top };
      default:
        return { x: right, y: bottom };
    }
  }

  // --- the loop -------------------------------------------------------------

  private tick = (now: number): void => {
    if (this.destroyed) return;
    this.raf = requestAnimationFrame(this.tick);
    this.lastRaf = now;
    this.step(now);
  };

  /**
   * The desktop overlay is a transparent, never-activated top-most window, so
   * Chromium can class it as occluded and stop firing rAF entirely — state
   * would update while nothing ever repainted. This timer keeps drawing.
   */
  private watchdog = (): void => {
    if (this.destroyed) return;
    const now = performance.now();
    if (now - this.lastRaf > 500) this.step(now);
  };

  private step(now: number): void {
    // Long frames (tab restore, blocked main thread) would otherwise teleport
    // every spring and desync the walk cycle.
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    if (dt <= 0) return;

    this.clock += dt;
    this.expireMinimize();
    this.accrueActiveTime(dt);
    this.stepBlink(now);

    if (this.interact === 'express' && now >= this.expressUntil) {
      this.endExpress();
    }

    if (this.interact === 'drag' || this.interact === 'press') {
      // Position is driven by the pointer; keep the lifted pose.
      if (this.interact === 'drag') {
        this.mode = 'walk';
        this.poseName = 'waiting';
      }
    } else if (this.interact === 'return-climb') {
      this.stepReturnClimb(dt);
    } else if (this.interact === 'return-run') {
      this.stepReturnRun(dt);
    } else if (this.interact === 'express') {
      // Pose was chosen on click; hold it until the timer ends.
      this.mode = this.reduced ? 'head' : 'walk';
    } else if (this.phase === 'idle') {
      this.stepIdle(now, dt);
    } else {
      this.stepBreak(dt);
    }

    // Direct-driven states write px/py themselves; springs would fight them.
    if (
      this.interact !== 'drag' &&
      this.interact !== 'return-climb' &&
      this.interact !== 'return-run'
    ) {
      advance(this.px, dt);
      advance(this.py, dt);
    }
    advance(this.gx, dt);
    advance(this.gy, dt);

    this.opacity += (this.targetOpacity() - this.opacity) * (1 - Math.pow(0.86, dt * 60));
    this.renderer?.apply(this.frame());
  }

  private targetOpacity(): number {
    if (!this.policyEnabled()) return 0;
    return this.prefs.minimized ? 0 : 1;
  }

  private frame(): FrameState {
    const walking = this.mode === 'walk';
    const dragging = this.interact === 'drag';
    let bob = 0;
    let squash = 1;
    if (this.reduced) {
      bob = 0;
    } else if (dragging) {
      // A soft float while held so he still feels alive in the hand.
      bob = Math.sin(this.clock * 5) * 3 - 6;
    } else if (this.interact === 'return-climb') {
      // Stretch a little as he climbs down the window.
      bob = Math.sin(this.clock * 10) * 2;
      squash = 1.04;
    } else if (this.interact === 'express') {
      bob = Math.sin(this.clock * 3.2) * 3;
    } else if (walking) {
      // Two bounces per stride, plus a light squash on each footfall.
      const p = (this.clock * 1000) / STEP_MS;
      bob = -Math.abs(Math.sin(p * Math.PI)) * 5;
      squash = 1 - Math.abs(Math.sin(p * Math.PI)) * 0.035;
    } else {
      bob = Math.sin(this.clock * 1.7) * 4;
    }

    return {
      mode: this.mode,
      x: this.px.value,
      y: this.py.value,
      gazeX: this.gx.value,
      gazeY: this.gy.value,
      blink: this.blink,
      yaw: this.gx.value,
      bob,
      squash,
      poseName: this.poseName,
      facing: this.facing,
      opacity: this.opacity,
      bubble: this.bubble,
      bubbleDismissible: !!this.adminMsg,
      dragging,
      // Restore chip only when the user tucked him away — not when the tenant
      // disabled Buntu entirely.
      minimized: this.prefs.minimized && this.policyEnabled(),
    };
  }

  // --- behaviour: idle floating head ---------------------------------------

  private stepIdle(now: number, _dt: number): void {
    this.mode = 'head';
    if (this.policy?.presence?.idle_floating_head === false) {
      // Still present, but no bob/gaze theatre.
      this.gx.target = 0;
      this.gy.target = 0;
      return;
    }
    const watching = this.policy?.presence?.cursor_watching !== false;
    const engaged =
      watching && this.pointer && now - this.lastPointerMove < IDLE_GLANCE_MS;

    if (engaged && this.pointer) {
      // Track the cursor relative to the head, normalised so the eyes reach
      // full deflection well before the pointer leaves the viewport.
      const reach = Math.max(240, window.innerWidth * GAZE_REACH);
      const size = this.renderer?.headSize ?? { w: 100, h: 132 };
      const eyeY = this.py.value - size.h * 0.62;
      this.gx.target = clamp((this.pointer.x - this.px.value) / reach, -1, 1);
      this.gy.target = clamp((this.pointer.y - eyeY) / (reach * 0.75), -1, 1);
      this.nextGlance = now + 700 + Math.random() * 900;
    } else if (now >= this.nextGlance) {
      // Disengaged: look around on its own, including the diagonals.
      const dirs: Array<[number, number]> = [
        [-0.85, 0], [0.85, 0], [0, -0.8], [-0.7, -0.6],
        [0.7, -0.6], [-0.6, 0.5], [0.6, 0.5], [0, 0],
      ];
      const [dx, dy] = dirs[Math.floor(Math.random() * dirs.length)];
      this.gx.target = dx;
      this.gy.target = dy;
      this.nextGlance = now + 1400 + Math.random() * 2200;
    }
  }

  private stepBlink(now: number): void {
    if (now >= this.nextBlink && this.blinkUntil < now) {
      this.blinkUntil = now + BLINK_DUR_MS;
      this.nextBlink =
        now + BLINK_MIN_MS + Math.random() * (BLINK_MAX_MS - BLINK_MIN_MS);
    }
    if (now < this.blinkUntil) {
      // Symmetric close/open over the blink window.
      const t = 1 - (this.blinkUntil - now) / BLINK_DUR_MS;
      this.blink = Math.sin(t * Math.PI);
    } else {
      this.blink = 0;
    }
  }

  // --- behaviour: hourly stretch break -------------------------------------

  /**
   * Counts time the user is actually here.
   *
   * Wall-clock would fire a reminder the moment someone returns from lunch,
   * so the tab must be visible and there must have been recent input.
   */
  /**
   * When a snooze/hide window ends, bring Buntu back on his own so he never
   * stays stuck behind the chip after the timer is over.
   */
  private expireMinimize(): void {
    if (!this.prefs.minimized) return;
    const now = Date.now();
    const hadTimer = !!(this.prefs.snoozedUntil || this.prefs.hiddenUntil);
    if (!hadTimer) return;
    const snoozeDone = !this.prefs.snoozedUntil || this.prefs.snoozedUntil < now;
    const hideDone = !this.prefs.hiddenUntil || this.prefs.hiddenUntil < now;
    if (snoozeDone && hideDone) {
      this.update({ minimized: false, snoozedUntil: null, hiddenUntil: null });
    }
  }

  private accrueActiveTime(dt: number): void {
    if (this.prefs.minimized) return;
    if (!this.policyWellnessEnabled()) return;
    if (this.interact !== 'none') return;
    if (this.inQuietHours() || this.inDnd()) return;
    const pauseIdle = this.policy?.wellness?.pause_when_idle !== false;
    if (pauseIdle && Date.now() - this.lastInput > AWAY_MS) return;
    this.activeMs += dt * 1000;

    if (this.phase !== 'idle') return;
    const snoozed = this.prefs.snoozedUntil && Date.now() < this.prefs.snoozedUntil;
    const hidden =
      this.prefs.hiddenUntil && Date.now() < this.prefs.hiddenUntil;
    if (snoozed || hidden) return;
    if (this.activeMs >= this.prefs.intervalMinutes * 60_000) this.beginBreak();
  }

  private beginBreak(): void {
    if (this.interact !== 'none') return;
    if (!this.policyWellnessEnabled()) return;
    if (this.inQuietHours() || this.inDnd()) return;
    this.activeMs = 0;
    this.renderer?.setMenuLocked(true);
    // Held until he stops walking; speaking mid-stride looks wrong.
    const lines = this.breakLines();
    this.line = lines[Math.floor(Math.random() * lines.length)];
    this.adminMsg = null;

    if (this.reduced) {
      // No walking about for users who asked for less motion: Buntu stays put
      // and just speaks.
      this.phase = 'talk';
      this.phaseT = 0;
      this.poseName = 'idle';
      this.bubble = this.line;
      return;
    }

    const vw = window.innerWidth;
    const fromRight = this.px.value > vw / 2;

    // Buntu walks out of his own corner rather than teleporting off-screen
    // and marching back in, so the whole break reads as one continuous move.
    this.mode = 'walk';
    this.phase = 'enter';
    this.phaseT = 0;
    this.facing = fromRight ? -1 : 1;
    this.stopX = fromRight ? vw * 0.58 : vw * 0.42;
    // The walk drives px directly, so take the spring out of the loop.
    this.px.stiffness = 1;
    this.py.target = window.innerHeight - MARGIN;
  }

  private stepBreak(dt: number): void {
    this.phaseT += dt;

    if (this.phase === 'talk') {
      if (this.adminMsg) {
        const byPri: Record<string, string> = {
          urgent: 'alert',
          important: 'puzzled',
          info: 'cheer',
        };
        this.poseName = byPri[this.adminMsg.priority] || 'cheer';
      } else {
        this.poseName = this.phaseT < 0.6 ? 'cheer' : 'idle';
      }
      // Admin broadcasts linger — a 6.5s bubble is easy to miss on a desktop.
      if (this.phaseT > (this.adminMsg ? 20 : 6.5)) {
        this.bubble = null;
        // Roll straight into the backlog without trekking back to the corner.
        if (this.adminMsg && this.messageQueue.length) {
          this.ackCurrentMessage();
          this.nextQueuedMessage();
          return;
        }
        if (this.reduced) {
          this.endBreak();
        } else {
          // Walk back to the corner he came from.
          this.phase = 'exit';
          this.phaseT = 0;
          this.stopX = this.homePosition().x;
          this.facing = this.stopX > this.px.value ? 1 : -1;
        }
      }
      return;
    }

    // Walking phases move at a constant speed rather than easing, so the
    // stride matches the footfall animation.
    const dir = this.facing;
    this.px.value += dir * WALK_SPEED * dt;
    this.px.target = this.px.value;
    // Alternating the two lean poses gives a waddle. There is no mirrored
    // frame per direction, and mirroring would reverse the chest logo.
    this.poseName =
      Math.floor((this.clock * 1000) / STEP_MS) % 2 === 0 ? 'move-left' : 'move-right';

    const reached = dir > 0 ? this.px.value >= this.stopX : this.px.value <= this.stopX;
    if (!reached) return;

    if (this.phase === 'enter') {
      this.px.value = this.stopX;
      this.phase = 'talk';
      this.phaseT = 0;
      this.bubble = this.line;
    } else {
      this.endBreak();
      this.nextQueuedMessage();
    }
  }

  /** Acknowledges the broadcast on screen so the Hub stops re-sending it. */
  private ackCurrentMessage(): void {
    const msg = this.adminMsg;
    const ack = this.onAdminAck;
    this.adminMsg = null;
    this.onAdminAck = null;
    if (!msg || !ack) return;
    try {
      ack(msg.id);
    } catch {
      /* ignore ack errors */
    }
  }

  private endBreak(unlockMenu = true): void {
    if (unlockMenu && this.interact === 'none') {
      this.renderer?.setMenuLocked(false);
    }
    this.ackCurrentMessage();
    this.bubble = null;
    this.mode = 'head';
    this.phase = 'idle';
    this.poseName = 'idle';
    this.facing = 1;
    this.px.stiffness = 0.14;
    this.py.stiffness = 0.14;
    // Ease rather than snap: for a top corner the head still has to float up
    // from the floor it just walked along.
    const home = this.homePosition();
    this.px.target = home.x;
    this.py.target = home.y;
  }

  destroy(): void {
    this.destroyed = true;
    cancelAnimationFrame(this.raf);
    window.clearInterval(this.watchdogTimer);
    window.clearTimeout(this.clickTimer);
    this.unbind();
    this.renderer?.destroy();
    this.renderer = null;
  }
}

const HISTORY_KEY = 'buntu.messageHistory.v1';
/** How many older messages each "Older messages" click reveals. */
const INBOX_PAGE = 20;
const HISTORY_MAX = 100;

function loadMessageHistory(): AdminMessage[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((m) => m?.id && m?.body) : [];
  } catch {
    return [];
  }
}

function saveMessageHistory(items: AdminMessage[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(items.slice(0, HISTORY_MAX)));
  } catch {
    /* ignore quota */
  }
}

function isToday(iso?: string | null): boolean {
  if (!iso) return true;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function formatMessageTime(iso?: string | null): string {
  if (!iso) return 'Just now';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  try {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return d.toISOString();
  }
}

function formatMessageWhen(iso?: string | null): string {
  if (!iso) return 'Just now';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  try {
    return d.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return d.toISOString();
  }
}
